import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

type TelemetryMetadata = {
  startedAt: number;
  requestId: string;
  path: string;
  method: string;
};

type TelemetryConfig = InternalAxiosRequestConfig & {
  telemetry?: TelemetryMetadata;
};

const UUID_PATTERN = /\b([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-([0-9a-f]{12})\b/gi;

const maskPath = (path: string) => path.replace(UUID_PATTERN, '$1-****-****-****-$2');

const getRequestId = (data: unknown) => {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const requestId = data.get('requestId');
    return requestId ? String(requestId) : '-';
  }
  return '-';
};

const getPath = (url?: string) => {
  if (!url) return '-';
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return maskPath(parsedUrl.pathname);
  } catch {
    return maskPath(url.split('?')[0]);
  }
};

const getCurrentUser = () => {
  try {
    const rawUser = sessionStorage.getItem('User');
    if (!rawUser) return 'not-authenticated';
    const user = JSON.parse(rawUser);
    return user.mail || user.sAMAccountName || 'authenticated-user';
  } catch {
    return 'unknown-user';
  }
};

const sendTelemetry = (event: Record<string, string | number>) => {
  const body = JSON.stringify(event);
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon('/ui-log', blob);
    return;
  }

  fetch('/ui-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
};

export const logAuthEvent = (action: string, outcome: string, message = '-') => {
  sendTelemetry({
    type: 'auth',
    action,
    outcome,
    user: getCurrentUser(),
    message,
  });
};

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.message || 'Axios request failed';
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
};

const getErrorType = (error: AxiosError) => {
  if (error.code === 'ECONNABORTED') return 'timeout';
  if (error.code === 'ERR_CANCELED') return 'canceled';
  if (error.response) return 'api_error_response';
  return 'network_error';
};

export const attachApiTelemetry = (api: AxiosInstance) => {
  api.interceptors.request.use((requestConfig: TelemetryConfig) => {
    const method = (requestConfig.method || 'GET').toUpperCase();
    const path = getPath(requestConfig.url);
    const requestId = getRequestId(requestConfig.data);

    requestConfig.telemetry = {
      startedAt: Date.now(),
      requestId,
      path,
      method,
    };

    sendTelemetry({
      phase: 'start',
      outcome: 'pending',
      method,
      path,
      requestId,
      user: getCurrentUser(),
    });

    return requestConfig;
  });

  api.interceptors.response.use(
    (response) => {
      const telemetry = (response.config as TelemetryConfig).telemetry;
      if (telemetry) {
        sendTelemetry({
          phase: 'success',
          outcome: 'success',
          method: telemetry.method,
          path: telemetry.path,
          requestId: telemetry.requestId,
          user: getCurrentUser(),
          status: response.status,
          durationMs: Date.now() - telemetry.startedAt,
        });
      }
      return response;
    },
    (error: AxiosError) => {
      const telemetry = (error.config as TelemetryConfig | undefined)?.telemetry;
      if (telemetry) {
        sendTelemetry({
          phase: 'failure',
          outcome: 'failure',
          errorType: getErrorType(error),
          method: telemetry.method,
          path: telemetry.path,
          requestId: telemetry.requestId,
          user: getCurrentUser(),
          status: error.response?.status || 'network',
          durationMs: Date.now() - telemetry.startedAt,
          message: getErrorMessage(error),
        });
      }
      return Promise.reject(error);
    }
  );
};
